export interface SearchRange {
  min: number;
  max: number;
}

export interface SearchState {
  currentFrequency: number;
  range: SearchRange;
  history: number[];
  confidence: number;
  iterations: number;
}

export class BinarySearchAlgorithm {
  private readonly MIN_FREQUENCY = 20;
  private readonly MAX_FREQUENCY = 20000;
  private readonly CONFIDENCE_THRESHOLD = 0.95;
  private readonly MIN_STEP_SIZE = 5; // Hz
  
  // Common tinnitus frequency ranges based on research
  private readonly DEFAULT_MIN = 125;    // P1 percentile
  private readonly DEFAULT_MAX = 16000;  // P99 percentile
  private readonly DEFAULT_START = 6125; // Statistical center of common tinnitus frequencies

  private state: SearchState;

  constructor(initialFrequency?: number, useExtendedRange: boolean = false) {
    // If no initial frequency provided, use the median
    const startFreq = initialFrequency || this.DEFAULT_START;
    
    // Use extended range for edge cases, default range for most users
    const minFreq = useExtendedRange ? this.MIN_FREQUENCY : this.DEFAULT_MIN;
    const maxFreq = useExtendedRange ? this.MAX_FREQUENCY : this.DEFAULT_MAX;
    
    this.state = {
      currentFrequency: startFreq,
      range: {
        min: minFreq,
        max: maxFreq,
      },
      history: [startFreq],
      confidence: 0,
      iterations: 0,
    };
  }

  /**
   * Process user response and calculate next frequency
   * @param response - 'higher', 'lower', or 'match'
   * @returns Next frequency to test or null if match found
   */
  processResponse(response: 'higher' | 'lower' | 'match'): number | null {
    this.state.iterations++;

    if (response === 'match') {
      this.state.confidence = 1;
      return null; // Found the match
    }

    // Update range based on response
    if (response === 'higher') {
      this.state.range.min = this.state.currentFrequency;
    } else {
      this.state.range.max = this.state.currentFrequency;
    }

    // Calculate next frequency (binary search)
    const nextFrequency = Math.round(
      (this.state.range.min + this.state.range.max) / 2
    );

    // Check if we're converging - need range ≤ 10 Hz for ±5 Hz accuracy
    const currentRangeSize = this.state.range.max - this.state.range.min;
    if (currentRangeSize <= 10) {
      this.state.confidence = 0.95;
      return nextFrequency; // Within ±5 Hz accuracy
    }

    // Update state
    this.state.currentFrequency = nextFrequency;
    this.state.history.push(nextFrequency);

    // Calculate confidence based on range reduction
    const totalRange = this.MAX_FREQUENCY - this.MIN_FREQUENCY;
    const currentRange = this.state.range.max - this.state.range.min;
    this.state.confidence = 1 - (currentRange / totalRange);

    return nextFrequency;
  }


  /**
   * Reset the search algorithm
   */
  reset(useExtendedRange: boolean = false): void {
    const minFreq = useExtendedRange ? this.MIN_FREQUENCY : this.DEFAULT_MIN;
    const maxFreq = useExtendedRange ? this.MAX_FREQUENCY : this.DEFAULT_MAX;
    
    this.state = {
      currentFrequency: this.DEFAULT_START,
      range: {
        min: minFreq,
        max: maxFreq,
      },
      history: [this.DEFAULT_START],
      confidence: 0,
      iterations: 0,
    };
  }

  /**
   * Get current state
   */
  getState(): Readonly<SearchState> {
    return { ...this.state };
  }

  /**
   * Check if search is complete
   */
  isComplete(): boolean {
    return this.state.confidence >= this.CONFIDENCE_THRESHOLD;
  }

  /**
   * Get progress percentage based on range convergence
   */
  getProgress(): number {
    if (this.state.iterations === 0) return 0;
    
    // Calculate progress based on how much the range has narrowed
    const initialRange = this.DEFAULT_MAX - this.DEFAULT_MIN; // 16000 - 125
    const currentRange = this.state.range.max - this.state.range.min;
    
    // Progress is how much we've narrowed the range
    const rangeProgress = 1 - (currentRange / initialRange);
    
    // Convert to percentage and ensure it reaches 100% when complete
    if (this.isComplete()) {
      return 100;
    }
    
    // Scale the progress to be more user-friendly (faster early progress)
    const scaledProgress = Math.pow(rangeProgress, 0.7); // Makes early progress faster
    
    return Math.min(95, Math.round(scaledProgress * 100));
  }

  /**
   * Set state (for restoring saved progress)
   */
  setState(partialState: {
    currentFrequency: number;
    minFreq: number;
    maxFreq: number;
  }): void {
    this.state.currentFrequency = partialState.currentFrequency;
    this.state.range.min = partialState.minFreq;
    this.state.range.max = partialState.maxFreq;
    this.state.history.push(partialState.currentFrequency);
    
    // Recalculate confidence
    const totalRange = this.MAX_FREQUENCY - this.MIN_FREQUENCY;
    const currentRange = this.state.range.max - this.state.range.min;
    this.state.confidence = 1 - (currentRange / totalRange);
  }
}